
import { jitsiLocalStorage } from '@jitsi/js-utils';
import React from 'react';
import { toArray } from 'react-emoji-render';

import logger from '../../../app/logger';
import Avatar from '../../../base/avatar/components/Avatar';
import { Dialog } from '../../../base/dialog';
import { translate } from '../../../base/i18n';
import { Icon, IconEdit, IconReply, IconRestore, IconSpinner, IconTrash } from '../../../base/icons';
import { getLocalParticipant, getParticipantById, isLocalParticipantModerator } from '../../../base/participants';
import { Linkify } from '../../../base/react';
import { connect } from '../../../base/redux';
import { getBackendSafeRoomName } from '../../../base/util';
import { GIF_PREFIX } from '../../../gifs/constants';
import { isGifMessage } from '../../../gifs/function.any';
import { DELETE_MESSAGE, RESTORE_MESSAGE } from '../../actionTypes';
import { openChat } from '../../actions';
import { deleteChatMessage, openImagePreview, restoreChatMessage, sendEditMessage } from '../../actions.any';
import { MESSAGE_RESTORE_TIMEOUT, MESSAGE_TYPE_LOCAL } from '../../constants';
import { getMessageText, postDeleteChatUploadedFile } from '../../functions';
import AbstractChatMessage, { type Props } from '../AbstractChatMessage';

import GifMessage from './GifMessage';

const IMAGE_PREVIEW_STYLES = {
    maxWidth: '100%',
    maxHeight: '300px',
    borderRadius: '8px',
    cursor: 'pointer',
    objectFit: 'contain'
};

const DOWNLOAD_BUTTON_STYLES = {
    marginTop: '8px',
    padding: '6px 12px',
    background: '#4b6790',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    whiteSpace: 'nowrap'
};

const FILE_ATTACHMENT_CURSOR_STYLES = {
    cursor: 'pointer'
};

/**
 * Renders a single chat message.
 */
class ChatMessage extends AbstractChatMessage<Props> {
    _isMounted: boolean;
    _restoreTimeout: any;

    /**
     * Initializes a new {@code ChatMessage} instance.
     *
     * @param {Props} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        this._isMounted = false;
        this._restoreTimeout = null;

        this.state = {
            showDeleteDialog: false,
            pendingDeleteData: null,
            editingMessageId: null,
            editingText: '',
            isDeleting: false
        };

        // Bind event handlers
        this._onDeleteDialogCancel = this._onDeleteDialogCancel.bind(this);
        this._onDeleteDialogConfirm = this._onDeleteDialogConfirm.bind(this);
        this._showDeleteDialog = this._showDeleteDialog.bind(this);
        this._onRestoreMessage = this._onRestoreMessage.bind(this);
        this._onEditClick = this._onEditClick.bind(this);
        this._onEditSave = this._onEditSave.bind(this);
        this._onEditCancel = this._onEditCancel.bind(this);
        this._isImageFile = this._isImageFile.bind(this);
        this._handleImageClick = this._handleImageClick.bind(this);
        this._canRestoreMessage = this._canRestoreMessage.bind(this);
        this._setupRestoreTimeout = this._setupRestoreTimeout.bind(this);
        this._onReplyClick = this._onReplyClick.bind(this);
    }

    /**
     * Sets up a timer to re-render when the restore window expires (1 minute).
     *
     * @param {Object} message - The chat message.
     * @returns {void}
     */
    _setupRestoreTimeout(message) {
        if (this._restoreTimeout) {
            clearTimeout(this._restoreTimeout);
            this._restoreTimeout = null;
        }

        if (message?.deleteChat && message?.deletedAt) {
            const remainingTime = MESSAGE_RESTORE_TIMEOUT - (Date.now() - message.deletedAt);

            if (remainingTime > 0) {
                this._restoreTimeout = setTimeout(() => {
                    if (this._isMounted) {
                        this.forceUpdate();
                    }
                }, remainingTime + 50);
            }
        }
    }

    /**
     * Checks whether a deleted message can still be restored within the 1-minute window.
     *
     * @param {Object} message - The message object.
     * @returns {boolean}
     */
    _canRestoreMessage(message) {
        if (!message?.deleteChat) {
            return false;
        }

        if (message.deletedAt) {
            return (Date.now() - message.deletedAt) <= MESSAGE_RESTORE_TIMEOUT;
        }

        return true;
    }

    /**
     * Implements React's {@link Component#componentDidMount()}.
     *
     * @inheritdoc
     */
    componentDidMount() {
        this._isMounted = true;
        this._setupRestoreTimeout(this.props.message);
    }

    /**
     * Implements React's {@link Component#componentDidUpdate()}.
     *
     * @inheritdoc
     */
    componentDidUpdate(prevProps: Props) {
        if (prevProps.message?.deleteChat !== this.props.message?.deleteChat
            || prevProps.message?.deletedAt !== this.props.message?.deletedAt) {
            this._setupRestoreTimeout(this.props.message);
        }
    }

    /**
     * Implements React's {@link Component#componentWillUnmount()}.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        this._isMounted = false;
        if (this._restoreTimeout) {
            clearTimeout(this._restoreTimeout);
            this._restoreTimeout = null;
        }
    }

    /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
    render() {
        const { message, t } = this.props;
        const { _isModerator } = this.props;
        const { editingMessageId, editingText, isDeleting } = this.state;
        const isEditing = editingMessageId === message.messageId;

        // Enable editing for local messages including private messages
        const canEdit = message.messageType === 'local' && message.messageId;

        // Add null checks to prevent crashes
        if (!message) {
            return null;
        }

        // Don't render a chat in chatbox which recipient is undefined
        if (message.recipient === undefined) {
            return null;
        }

        const isDeleted = Boolean(message.deleteChat);

        const txt = this._getMessageText();
        const text = getMessageText(message);
        const isFileAttachment = Boolean(text && (text.includes('class="file-attachment"') || text.includes('<a ')));
        const isGifOrFile = !isDeleted && (isGifMessage(text) || isFileAttachment);
        const showEditButton = !isDeleted && canEdit && !isGifOrFile;

        // Content is an array of text and emoji components
        const content = [];

        if (!isDeleted) {
            if (isGifMessage(text)) {
                const url = text.substring(GIF_PREFIX.length, text.length - 1);

                content.push(<GifMessage
                    key = { url }
                    url = { url } />);
            } else if (isFileAttachment) {
                // Parse file attachment from HTML
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, 'text/html');
                const fileLink = doc.querySelector('a.file-attachment');

                if (fileLink) {
                    const fileName = fileLink.textContent.replace(/^🖼️\s*/, '').trim();
                    const fileUrl = fileLink.getAttribute('href');
                    const downloadName = fileLink.getAttribute('download') || fileName;
                    const isImage = this._isImageFile(fileName);
                    const canPreview = this._canPreviewFile(fileName);

                    content.push(
                        <div
                            className = 'file-attachment-wrapper'
                            key = 'file-attachment'>
                            {isImage ? (
                                <div className = 'image-attachment-preview'>
                                    <img
                                        alt = { fileName }
                                        className = 'chat-image-preview'
                                        onClick = { () => this._handleImageClick(fileUrl, fileName) }
                                        src = { fileUrl }
                                        style = { IMAGE_PREVIEW_STYLES } />
                                    <div className = 'image-attachment-actions'>
                                        <button
                                            className = 'image-download-btn'
                                            onClick = { event => this._downloadFile(fileUrl, downloadName, event) }
                                            style = { DOWNLOAD_BUTTON_STYLES }>
                                            📥 {t('welcomepage.footer.download')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className = 'file-attachment-modern'
                                    onClick = { event => {
                                        if (canPreview) {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            this._handleImageClick(fileUrl, fileName);
                                        } else {
                                            this._downloadFile(fileUrl, downloadName, event);
                                        }
                                    } }
                                    style = { FILE_ATTACHMENT_CURSOR_STYLES }>
                                    <div className = 'file-icon-large'>
                                        {this._getFileIcon(fileName)}
                                    </div>
                                    <div className = 'file-name-below'>
                                        {fileName}
                                    </div>
                                    <div className = 'file-attachment-actions'>
                                        <button
                                            className = 'file-download-btn'
                                            onClick = { event => this._downloadFile(fileUrl, downloadName, event) }
                                            style = { DOWNLOAD_BUTTON_STYLES }>
                                            📥 {t('welcomepage.footer.download')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                }
            } else {
                const tokens = txt.split(' ');

                for (let i = 0; i < tokens.length; i++) {
                    const token = tokens[i];

                    if (token.includes('://')) {
                        content.push(token);
                    } else {
                        content.push(...toArray(token, { className: 'smiley' }));
                    }
                    if (i < tokens.length - 1) {
                        content.push(' ');
                    }
                }
            }
        }

        const processedMessage = content.map((item, index) => {
            if (typeof item === 'string') {
                return (
                    <span key = { `${index}-${item}` }>
                        <Linkify>{item}</Linkify>
                    </span>
                );
            }

            return React.cloneElement(item, { key: `${index}-emoji` });
        });

        const size = 35;

        return (
            <>
                <div
                    className = 'chatmessage-wrapper'
                    style = {{
                        margin: `${this.props.showDisplayName ? '' : message.messageType === 'local' ? '0 35px 0 0' : '0 0 0 35px'}`
                    }}>
                    <div
                        className = { `chatmessage ${message.privateMessage ? 'privatemessage' : ''
                        }` }>
                        <div
                            className = 'replywrapper'
                            style = {{
                                flexDirection: `${message.messageType === 'local' ? 'row-reverse' : 'row'
                                }`
                            }}>
                            {this.props.showDisplayName && (
                                <Avatar
                                    className = 'preview-avatar'
                                    id = { message.id }
                                    participantId = { message.id }
                                    size = { size } />
                            )}
                            <div
                                className = 'messagecontent'
                                id = { `${message.messageType === 'local' ? 'localchat' : ''}` }>
                                {this.props.showDisplayName && this._renderDisplayName()}
                                {isDeleted ? (
                                    <>
                                        <div
                                            className = 'usermessage deleted-message'
                                            style = {{
                                                background: `${message.messageType === 'local' ? '#4b6790' : ''}`,
                                                color: `${message.messageType === 'local' ? 'rgba(255, 255, 255, 0.75)' : 'rgba(0, 0, 0, 0.5)'}`
                                            }}>
                                            <span>
                                                {message.messageType === 'local'
                                                    ? t('chat.youDeletedMessage')
                                                    : t('chat.messageDeleted')}
                                            </span>
                                        </div>
                                        {message.privateMessage && this._renderPrivateNotice()}
                                    </>
                                ) : isEditing ? (
                                    <div className = 'chatmessage-edit-wrapper'>
                                        <textarea
                                            autoFocus = { true }
                                            className = 'chatmessage-edit-input'
                                            maxLength = { 1000 }
                                            onChange = { e => this.setState({ editingText: e.target.value }) }
                                            rows = { 3 }
                                            value = { editingText } />
                                        <div className = 'usermsg-length'>
                                            {editingText ? editingText.length : 0} / 1000
                                        </div>
                                        <div className = 'chatmessage-edit-actions'>
                                            <button
                                                className = 'chatmessage-edit-save'
                                                onClick = { this._onEditSave }>
                                                {t('dialog.confirm')}
                                            </button>
                                            <button
                                                className = 'chatmessage-edit-cancel'
                                                onClick = { this._onEditCancel }>
                                                {t('dialog.Cancel')}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div
                                            className = { `usermessage${isDeleting ? ' is-deleting' : ''}` }
                                            style = {{
                                                background: `${message.messageType === 'local' ? '#4b6790' : ''
                                                }`,
                                                color: `${message.messageType === 'local' ? 'white' : ''}`
                                            }}>
                                            {isDeleting && (
                                                <div className = 'message-deleting-overlay'>
                                                    <span className = 'deleting-spinner'>
                                                        <Icon
                                                            size = { 24 }
                                                            src = { IconSpinner } />
                                                    </span>
                                                    <span className = 'deleting-text'>
                                                        {t('chat.fileUpload.deleting') || 'Deleting file...'}
                                                    </span>
                                                </div>
                                            )}
                                            <span className = 'sr-only'>
                                                {this.props.message.displayName
                                                    === this.props.message.recipient
                                                    ? t('chat.messageAccessibleTitleMe')
                                                    : t('chat.messageAccessibleTitle', {
                                                        user: this.props.message.displayName
                                                    })}
                                            </span>
                                            <span>{processedMessage}</span>
                                            {message.isEdited && (
                                                <span className = 'message-edited'>
                                                    {t('chat.messageEdited')}
                                                </span>
                                            )}
                                        </div>
                                        {message.privateMessage && this._renderPrivateNotice()}
                                    </>
                                )}
                            </div>

                            {isDeleted
                                ? _isModerator && !isFileAttachment && this._canRestoreMessage(message) && (
                                    <div className = 'chatmessage-actions'>
                                        <button
                                            className = 'restore-message'
                                            onClick = { () => this._onRestoreMessage(message.messageId) }
                                            title = { t('chat.restoreMessageTitle') }>
                                            <Icon
                                                color = '#fff'
                                                src = { IconRestore } />
                                        </button>
                                    </div>
                                )
                                : !isDeleting && (
                                    showEditButton
                                    || _isModerator
                                    || message.messageType === 'local'
                                    || (!this.props._isPrivateChatDisabled
                                        && message.privateMessage
                                        && message.messageType !== MESSAGE_TYPE_LOCAL)
                                ) && (
                                    <div className = 'chatmessage-actions'>
                                        {showEditButton && !isEditing && (
                                            <button
                                                className = 'edit-message'
                                                onClick = { this._onEditClick }
                                                title = { t('chat.editMessage') }>
                                                <Icon
                                                    color = '#fff'
                                                    src = { IconEdit } />
                                            </button>
                                        )}
                                        {(_isModerator || message.messageType === 'local') && (
                                            <button
                                                className = 'delete-message'
                                                onClick = { () =>
                                                    this._showDeleteDialog(message.messageId, message.message)
                                                }>
                                                <Icon
                                                    color = '#fff'
                                                    src = { IconTrash } />
                                            </button>
                                        )}
                                        {!this.props._isPrivateChatDisabled
                                            && message.privateMessage
                                            && message.messageType !== MESSAGE_TYPE_LOCAL && (
                                            <button
                                                className = 'reply-message'
                                                onClick = { this._onReplyClick }
                                                title = { t('toolbar.privateMessage') }>
                                                <Icon
                                                    color = '#fff'
                                                    src = { IconReply } />
                                            </button>
                                        )}
                                    </div>
                                )}
                        </div>
                    </div>
                    { this.props.showTimestamp && this._renderTimestamp() }
                </div>
                {this._renderDeleteDialog()}
            </>
        );
    }

    _getFormattedTimestamp: () => string;

    _getMessageText: () => string;

    _getPrivateNoticeMessage: () => string;

    /**
     * Checks if a file is an image based on its extension.
     *
     * @param {string} fileName - The name of the file
     * @returns {boolean} - True if the file is an image
     */
    _isImageFile(fileName) {
        const extension = fileName.split('.').pop()
            ?.toLowerCase();

        return [ 'jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico' ].includes(extension);
    }

    /**
     * Checks if a file can be previewed.
     *
     * @param {string} fileName - The name of the file
     * @returns {boolean} - True if the file can be previewed
     */
    _canPreviewFile(fileName) {
        const extension = fileName.split('.').pop()
            ?.toLowerCase();

        const previewableFormats = [
            'jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico',
            'mp4', 'webm', 'ogg', 'mov',
            'mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a',
            'pdf', 'txt', 'md', 'json', 'xml', 'csv', 'log'
        ];

        return previewableFormats.includes(extension);
    }

    /**
     * Handles file click to open preview modal.
     *
     * @param {string} fileUrl - The URL of the file to preview
     * @param {string} fileName - The name of the file
     * @returns {void}
     */
    _handleImageClick(fileUrl, fileName) {
        this.props.dispatch(openImagePreview(fileUrl, fileName));
    }


    /**
   * Downloads a file in the background without leaving the page.
   *
   * @param {string} fileUrl - The URL of the file to download
   * @param {string} fileName - The name to save the file as
   */
    async _downloadFile(fileUrl, fileName, event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        try {
            const response = await fetch(fileUrl);

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = blobUrl;
            link.download = fileName || 'download';
            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
                if (link.parentNode) {
                    document.body.removeChild(link);
                }
                window.URL.revokeObjectURL(blobUrl);
            }, 20000);
        } catch {
            const fallbackLink = document.createElement('a');

            fallbackLink.href = fileUrl;
            fallbackLink.download = fileName || 'download';
            fallbackLink.target = '_blank';
            document.body.appendChild(fallbackLink);
            fallbackLink.click();
            setTimeout(() => {
                if (fallbackLink.parentNode) {
                    document.body.removeChild(fallbackLink);
                }
            }, 1000);
        }
    }

    /**
   * Gets the appropriate file icon based on file extension.
   *
   * @param {string} fileName - The name of the file
   * @returns {string} - The emoji icon for the file type
   */
    _getFileIcon(fileName) {
        const extension = fileName.split('.').pop()
            ?.toLowerCase();

        switch (extension) {
        case 'pdf':
            return '📄';
        case 'doc':
        case 'docx':
            return '📝';
        case 'xls':
        case 'xlsx':
            return '📊';
        case 'ppt':
        case 'pptx':
            return '📈';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'svg':
        case 'webp':
            return '🖼️';
        case 'mp4':
        case 'mov':
        case 'avi':
            return '🎥';
        case 'mp3':
        case 'wav':
        case 'flac':
            return '🎵';
        case 'zip':
        case 'rar':
        case '7z':
            return '📦';
        case 'txt':
            return '📄';
        default:
            return '📎';
        }
    }

    /**
   * Renders the display name of the sender.
   *
   * @returns {React$Element<*>}
   */
    _renderDisplayName() {
        return <div className = 'display-name'>{this.props.message.displayName}</div>;
    }

    /**
     * Handles clicking the reply button to reply to a private message.
     *
     * @returns {void}
     */
    _onReplyClick() {
        const { message, _participants, dispatch } = this.props;
        const participant = getParticipantById(_participants, message.id);

        if (participant) {
            dispatch(openChat(participant));
        }
    }

    /**
     * Starts editing this message (inline).
     * @returns {void}
     */
    _onEditClick() {
        this.setState({
            editingMessageId: this.props.message.messageId,
            editingText: getMessageText(this.props.message)
        });
    }

    /**
     * Saves the edited message (XEP-0308, Prosody).
     * @returns {void}
     */
    _onEditSave() {
        const { message } = this.props;
        const { editingText } = this.state;

        if (message.messageId && editingText.trim()) {
            // For private messages, use the recipientId, otherwise undefined for group messages
            const recipientId = message.privateMessage ? message.recipientId : undefined;

            this.props.dispatch(sendEditMessage(
                message.messageId,
                editingText.trim(),
                recipientId
            ));
        }
        this.setState({ editingMessageId: null,
            editingText: '' });
    }

    /**
     * Cancels editing.
     * @returns {void}
     */
    _onEditCancel() {
        this.setState({ editingMessageId: null,
            editingText: '' });
    }

    /**
     * Shows the delete confirmation dialog.
     * @param {string} messageId - The id of the message to delete.
     * @param {string} message - The message text to be deleted.
     * @returns {void}
     */
    _showDeleteDialog(messageId, message) {
        this.setState({
            showDeleteDialog: true,
            pendingDeleteData: {
                messageId,
                message
            }
        });
    }

    /**
     * Handles the cancel action for the delete dialog.
     * @returns {void}
     */
    _onDeleteDialogCancel() {
        this.setState({
            showDeleteDialog: false,
            pendingDeleteData: null
        });
    }

    /**
     * Handles the confirm action for the delete dialog.
     * @returns {void}
     */
    _onDeleteDialogConfirm() {
        const { pendingDeleteData } = this.state;

        if (pendingDeleteData) {
            this.confirmDeleteMessage(
                pendingDeleteData.messageId,
                pendingDeleteData.message
            );
        }

        this.setState({
            showDeleteDialog: false,
            pendingDeleteData: null
        });
    }

    /**
     * Renders the delete confirmation dialog.
     * @returns {React$Element<*>|null}
     */
    _renderDeleteDialog() {
        const { showDeleteDialog } = this.state;

        if (!showDeleteDialog) {
            return null;
        }

        return (
            <Dialog
                cancelKey = { 'dialog.Cancel' }
                okKey = { 'dialog.Yes' }
                onCancel = { this._onDeleteDialogCancel }
                onSubmit = { this._onDeleteDialogConfirm }
                titleKey = { 'chat.deleteMessage' }
                width = { 'small' }>
                <span>
                    {this.props.t('toolbar.accessibilityLabel.deleteChat')}
                </span>
            </Dialog>
        );
    }


    /**
     * Deletes a chat message.
     * @param {string} messageId - The id of the message to delete.
     * @param {string} message - The message text to be deleted.
     * @returns {void}
     */
    async confirmDeleteMessage(messageId, message) {
        const conference = APP.store.getState()['features/base/conference'].conference;
        const myId = conference?.myUserId();
        const deletedAt = Date.now();

        // Check if message is a file attachment
        const isFileAttachment = message && message.includes('class="file-attachment"');

        try {
            if (isFileAttachment) {
                this.setState({ isDeleting: true });

                // Extract the URL from the href attribute
                const parser = new DOMParser();
                const doc = parser.parseFromString(message, 'text/html');
                const fileLink = doc.querySelector('a.file-attachment');

                if (fileLink) {
                    const fileUrl = fileLink.getAttribute('href');

                    try {
                        const payloadData = {
                            mhClientID: this.props.MH_CLIENT_ID,
                            meetingID: this.props.MeetingID,
                            accessToken: this.props.accesTokn,
                            config: this.props.config,
                            uploadedS3Url: fileUrl
                        };

                        await postDeleteChatUploadedFile(payloadData);
                    } catch (error) {
                        logger.error('Failed to delete file from server', error);
                    }
                }
            }

            // sending delete message action to the conference
            conference?.sendMessage({
                name: 'DELETE_MESSAGE',
                type: DELETE_MESSAGE,
                messageId,
                deletedAt,
                senderId: myId
            });

            if (this.props.message?.privateMessage && this.props.message?.recipientId) {
                conference?.sendPrivateTextMessage(this.props.message.recipientId, '__MEETHOUR_DELETED__', 'body', messageId);
            } else {
                conference?.sendTextMessage('__MEETHOUR_DELETED__', 'body', messageId);
            }

            this.props.dispatch(deleteChatMessage(messageId, deletedAt));
        } finally {
            if (this._isMounted) {
                this.setState({ isDeleting: false });
            }
        }
    }

    /**
     * Restores a deleted chat message.
     * @param {string} messageId - The id of the message to restore.
     * @returns {void}
     */
    _onRestoreMessage(messageId) {
        if (!messageId) {
            return;
        }

        if (!this._canRestoreMessage(this.props.message)) {
            return;
        }

        const text = getMessageText(this.props.message);
        const isFileAttachment = Boolean(text && (text.includes('class="file-attachment"') || text.includes('<a ')));

        if (isFileAttachment) {
            return;
        }

        const conference = APP.store.getState()['features/base/conference'].conference;
        const myId = conference?.myUserId();

        // sending restore message action to the conference
        conference?.sendMessage({
            name: 'RESTORE_MESSAGE',
            type: RESTORE_MESSAGE,
            messageId,
            senderId: myId
        });

        if (this.props.message?.privateMessage && this.props.message?.recipientId) {
            conference?.sendPrivateTextMessage(this.props.message.recipientId, '__MEETHOUR_RESTORED__', 'body', messageId);
        } else {
            conference?.sendTextMessage('__MEETHOUR_RESTORED__', 'body', messageId);
        }

        this.props.dispatch(restoreChatMessage(messageId));
    }

    /**
   * Renders the message privacy notice.
   *
   * @returns {React$Element<*>}
   */
    _renderPrivateNotice() {
        return (
            <div className = 'privatemessagenotice'>
                {this._getPrivateNoticeMessage()}
            </div>
        );
    }

    /**
   * Renders the time at which the message was sent.
   *
   * @returns {React$Element<*>}
   */
    _renderTimestamp() {
        return (<div
            className = 'timestamp'
            style = {{
                margin: `${this.props.showDisplayName ? '0 40px' : '0 10px'}`
            }}>{this._getFormattedTimestamp()}</div>);
    }


}

/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code Conference} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state) {
    const _participants = state['features/base/participants'];
    const _localParticipant = getLocalParticipant(state);
    const _isModerator = isLocalParticipantModerator(state);

    const { accessToken } = state['features/base/jwt'];
    const userAccessToken = jitsiLocalStorage.getItem('accessToken');
    const accesTokn = accessToken ?? userAccessToken;
    const { MH_CLIENT_ID } = state['features/base/config'];
    const MeetingID = getBackendSafeRoomName(state['features/base/conference'].room);
    const config = state['features/base/config'];

    const _isPrivateChatDisabled = state['features/chat']?.isPrivateChatDisabled || false;

    return {
        _isModerator,
        _localParticipant,
        _participants,
        _isPrivateChatDisabled,
        accesTokn,
        MH_CLIENT_ID,
        MeetingID,
        config
    };
}

export default connect(_mapStateToProps)(translate(ChatMessage));
